"use client"

import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircle2 } from "lucide-react"
import { z } from "zod"
import { memberSchema } from "@/schemas/member-schema"
import api from "@/apis"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"

type MemberFormValues = z.infer<typeof memberSchema>

export default function BecomeMember() {
  const { mutateAsync, isPending } = api.Member.Create.useMutation()

  const form = useForm<MemberFormValues>({
    resolver: zodResolver(memberSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      zipCode: "",
      city: "",
      country: "",
      joinReason: "",
    },
  })

  const onSubmit = async (values: MemberFormValues) => {
    try {
      await mutateAsync(values)
      form.reset()
    } catch (error) {
      console.error(error)
      // Error handling is managed by the API hook (toast)
    }
  }

  return (
    <main className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-foreground mb-6">Become a Member</h1>
          <p className="text-xl text-foreground/80">Join our community and support street children in Ethiopia.</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Benefits */}
            <div className="md:col-span-1">
              <h2 className="text-2xl font-bold mb-6">Member Benefits</h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle2 className="text-primary flex-shrink-0" size={20} />
                  <p className="text-foreground/90">Direct impact on children&apos;s lives</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="text-primary flex-shrink-0" size={20} />
                  <p className="text-foreground/90">Regular updates on our work</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="text-primary flex-shrink-0" size={20} />
                  <p className="text-foreground/90">Annual reports and newsletters</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="text-primary flex-shrink-0" size={20} />
                  <p className="text-foreground/90">Be part of a compassionate community</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="text-primary flex-shrink-0" size={20} />
                  <p className="text-foreground/90">Support sustainable long-term change</p>
                </div>
              </div>

              <div className="mt-8 p-6 bg-primary/10 rounded-lg">
                <p className="font-semibold text-lg text-foreground mb-2">€10 per month</p>
                <p className="text-foreground/80 text-sm">
                  Our membership fees go directly toward operating our day care centers and supporting the children.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="md:col-span-2">
              <div className="bg-card p-8 rounded-lg border border-border">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="John" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email *</FormLabel>
                          <FormControl>
                            <Input placeholder="john.doe@example.com" type="email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input placeholder="+1234567890" type="tel" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address *</FormLabel>
                          <FormControl>
                            <Input placeholder="123 Main St" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="zipCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Zip Code *</FormLabel>
                            <FormControl>
                              <Input placeholder="10001" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>City *</FormLabel>
                            <FormControl>
                              <Input placeholder="New York" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Country *</FormLabel>
                            <FormControl>
                              <Input placeholder="USA" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="joinReason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Why do you want to join?</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Tell us why you want to support our cause..."
                              className="min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" disabled={isPending} className="w-full">
                      {isPending ? "Submitting..." : "Become a Member"}
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
